/**
 * TypeScript types for the LLM provider-selection policy API.
 *
 * Mirrors `api/src/llm_config/policy_schemas.py` (ADR-042 §9).
 */

export type PolicyStrategy =
  | 'priority'
  | 'cost'
  | 'latency'
  | 'round_robin'
  | 'weighted'
  | 'sticky_tenant';

export type ByopPolicy =
  | 'prefer_platform'
  | 'prefer_byop'
  | 'only_byop'
  | 'only_platform';

export type HealthThreshold = 'skip_degraded' | 'allow_degraded';

// ---------------------------------------------------------------------------
// Sprint 278 (ROUTE-PIPE-1/A — REQ-LLM-006 §8.1): pipeline policy fields
// ---------------------------------------------------------------------------

export type RoutingMode = 'auto' | 'direct' | 'planned';
export type RoutingOnFailure =
  | 'degrade_direct'
  | 'fail_closed'
  | 'skip_stage'
  | 'preserve_mode';
export type PlanningOnFailure = 'skip' | 'fail_closed';
export type GroundingCheck = 'off' | 'warn' | 'enforce';
export type FrontierOnUnavailable = 'standard' | 'fail_closed';
export type PlanReview = 'off' | 'high_risk' | 'always';

// ---------------------------------------------------------------------------
// Sprint 280 (CHAT-TOK-1/B — REQ-LLM-006 rev. 16-Oct-2026, ADR-120):
// campos de economía de tokens (nombres exactos de LLD §8.1)
// ---------------------------------------------------------------------------

export type EstimatorId = 'tiktoken-cl100k_base';
export type DashboardWindow = '24h' | '7d' | '30d';
export type PromptCacheTtl = 'auto' | '5m' | '1h';
export type PromptCachePartition = 'tenant' | 'tenant_agent';
export type ToolExposure = 'stable' | 'sticky_working_set' | 'deferred';
export type OutputEffort = 'provider_default' | 'low' | 'medium' | 'high';
export type AsyncServiceTier = 'standard' | 'flex' | 'batch';
export type AsyncTierOnUnavailable = 'retry_standard' | 'wait_retry';
export type BudgetAwareProfile = 'off' | 'notify' | 'tighten';

/** Campos §8.1 rev. (forma plana usada por la API de política DR/tenant). */
export interface TokenEconomicsPolicyFields {
  measurement_enabled?: boolean | null;
  estimator_id?: EstimatorId | null;
  compare_tolerance_pct?: number | null;
  estimator_calibration?: boolean | null;
  count_tokens_sample_pct?: number | null;
  ledger_retention_days?: number | null;
  dashboard_window_default?: DashboardWindow | null;
  semantic_compression_allow?: boolean | null;
  platform_quality_holdout_pct?: number | null;
  optimization_holdout_pct?: number | null;
  prompt_cache_enabled?: boolean | null;
  prompt_cache_ttl?: PromptCacheTtl | null;
  prompt_cache_partition?: PromptCachePartition | null;
  prompt_cache_shared_platform_allowed?: boolean | null;
  prompt_cache_extended_retention_allowed?: boolean | null;
  tool_exposure?: ToolExposure | null;
  tool_deferred_threshold_tokens?: number | null;
  compaction_trigger_pct?: number | null;
  compaction_target_pct?: number | null;
  compaction_on_cold_cache?: boolean | null;
  output_effort?: OutputEffort | null;
  output_effort_dynamic?: boolean | null;
  async_service_tier?: AsyncServiceTier | null;
  async_tier_on_unavailable?: AsyncTierOnUnavailable | null;
  budget_aware_profile?: BudgetAwareProfile | null;
  budget_aware_threshold_pct?: number | null;
}

/** Campos §8.1 (forma plana usada por la API de política DR/tenant). */
export interface PipelinePolicyFields extends TokenEconomicsPolicyFields {
  routing_enabled?: boolean | null;
  routing_mode?: RoutingMode | null;
  routing_on_failure?: RoutingOnFailure | null;
  routing_confidence_threshold?: number | null;
  routing_timeout_ms?: number | null;
  planning_on_failure?: PlanningOnFailure | null;
  grounding_check?: GroundingCheck | null;
  routing_budget_tokens?: number | null;
  optimization_enabled?: boolean | null;
  max_loop_iterations?: number | null;
  frontier_agents_enabled?: boolean | null;
  frontier_max_duration_minutes?: number | null;
  frontier_max_concurrent_runs?: number | null;
  frontier_on_unavailable?: FrontierOnUnavailable | null;
  plan_review?: PlanReview | null;
  retrieval_gating_enabled?: boolean | null;
  foreground_timeout_seconds?: number | null;
  delegation_sync_timeout_seconds?: number | null;
  delegation_background_timeout_seconds?: number | null;
  fanout_aggregation_timeout_seconds?: number | null;
}

export const ROUTING_MODES: RoutingMode[] = ['auto', 'direct', 'planned'];
export const ROUTING_ON_FAILURES: RoutingOnFailure[] = [
  'degrade_direct',
  'fail_closed',
  'skip_stage',
  'preserve_mode',
];
export const PLANNING_ON_FAILURES: PlanningOnFailure[] = ['skip', 'fail_closed'];
export const GROUNDING_CHECKS: GroundingCheck[] = ['off', 'warn', 'enforce'];
export const FRONTIER_ON_UNAVAILABLE_VALUES: FrontierOnUnavailable[] = [
  'standard',
  'fail_closed',
];
export const PLAN_REVIEWS: PlanReview[] = ['off', 'high_risk', 'always'];

// Sprint 280 (CHAT-TOK-1/B): dominios de economía de tokens.
export const ESTIMATOR_IDS: EstimatorId[] = ['tiktoken-cl100k_base'];
export const DASHBOARD_WINDOWS: DashboardWindow[] = ['24h', '7d', '30d'];
export const PROMPT_CACHE_TTLS: PromptCacheTtl[] = ['auto', '5m', '1h'];
export const PROMPT_CACHE_PARTITIONS: PromptCachePartition[] = [
  'tenant',
  'tenant_agent',
];
export const TOOL_EXPOSURES: ToolExposure[] = [
  'stable',
  'sticky_working_set',
  'deferred',
];
export const OUTPUT_EFFORTS: OutputEffort[] = [
  'provider_default',
  'low',
  'medium',
  'high',
];
export const ASYNC_SERVICE_TIERS: AsyncServiceTier[] = [
  'standard',
  'flex',
  'batch',
];
export const ASYNC_TIER_ON_UNAVAILABLE_VALUES: AsyncTierOnUnavailable[] = [
  'retry_standard',
  'wait_retry',
];
export const BUDGET_AWARE_PROFILES: BudgetAwareProfile[] = [
  'off',
  'notify',
  'tighten',
];

/** Columna «Capas» de REQ-LLM-006 rev.: dónde puede fijarse cada campo. */
export type PolicyLayer = 'dr' | 'tenant' | 'hive' | 'agent';

export const TOKEN_ECONOMICS_LAYERS: Record<
  keyof TokenEconomicsPolicyFields,
  PolicyLayer[]
> = {
  measurement_enabled: ['dr', 'tenant', 'hive', 'agent'],
  estimator_id: ['dr'],
  compare_tolerance_pct: ['dr', 'tenant', 'hive', 'agent'],
  estimator_calibration: ['dr', 'tenant'],
  count_tokens_sample_pct: ['dr'],
  ledger_retention_days: ['dr'],
  dashboard_window_default: ['dr', 'tenant'],
  semantic_compression_allow: ['dr', 'tenant'],
  platform_quality_holdout_pct: ['dr'],
  optimization_holdout_pct: ['tenant', 'hive', 'agent'],
  prompt_cache_enabled: ['dr', 'tenant', 'hive', 'agent'],
  prompt_cache_ttl: ['dr', 'tenant', 'hive', 'agent'],
  prompt_cache_partition: ['dr', 'tenant', 'hive', 'agent'],
  prompt_cache_shared_platform_allowed: ['dr'],
  prompt_cache_extended_retention_allowed: ['dr', 'tenant'],
  tool_exposure: ['dr', 'tenant', 'hive', 'agent'],
  tool_deferred_threshold_tokens: ['dr', 'tenant', 'hive', 'agent'],
  compaction_trigger_pct: ['dr', 'tenant', 'hive', 'agent'],
  compaction_target_pct: ['dr', 'tenant', 'hive', 'agent'],
  compaction_on_cold_cache: ['dr', 'tenant', 'hive', 'agent'],
  output_effort: ['dr', 'tenant', 'hive', 'agent'],
  output_effort_dynamic: ['dr', 'tenant', 'hive', 'agent'],
  async_service_tier: ['dr', 'tenant', 'hive', 'agent'],
  async_tier_on_unavailable: ['dr', 'tenant'],
  budget_aware_profile: ['tenant'],
  budget_aware_threshold_pct: ['tenant'],
};

/** Campos con consumidor en F2/F3 (ADR-120 D9) — llevan indicación de fase. */
export const PHASE_F2_FIELDS: Array<keyof TokenEconomicsPolicyFields> = [
  'tool_exposure',
  'tool_deferred_threshold_tokens',
  'compaction_trigger_pct',
  'compaction_target_pct',
  'compaction_on_cold_cache',
  'output_effort',
  'output_effort_dynamic',
  'async_service_tier',
  'async_tier_on_unavailable',
];
export const PHASE_F3_FIELDS: Array<keyof TokenEconomicsPolicyFields> = [
  'budget_aware_profile',
  'budget_aware_threshold_pct',
];
export const PHASE_OF_FIELD: Partial<
  Record<keyof TokenEconomicsPolicyFields, 'F2' | 'F3'>
> = {
  ...Object.fromEntries(PHASE_F2_FIELDS.map((f) => [f, 'F2'])),
  ...Object.fromEntries(PHASE_F3_FIELDS.map((f) => [f, 'F3'])),
} as Partial<Record<keyof TokenEconomicsPolicyFields, 'F2' | 'F3'>>;

/** Techos duros de plataforma (§8.1) — visibles en los formularios. */
export const PIPELINE_CAPS: Record<string, number> = {
  routing_timeout_ms: 5000,
  max_loop_iterations: 10,
  frontier_max_duration_minutes: 4320,
  frontier_max_concurrent_runs: 50,
  foreground_timeout_seconds: 3600,
  delegation_sync_timeout_seconds: 3600,
  delegation_background_timeout_seconds: 28800,
  fanout_aggregation_timeout_seconds: 28800,
  // Sprint 280 (CHAT-TOK-1/B — REQ-LLM-006 rev., columna «Regla»).
  compare_tolerance_pct: 100,
  count_tokens_sample_pct: 5,
  ledger_retention_days: 365,
  platform_quality_holdout_pct: 5,
  optimization_holdout_pct: 10,
  compaction_trigger_pct: 95,
  compaction_target_pct: 95,
  budget_aware_threshold_pct: 100,
};

/** Defaults del baseline (referencia visual en los formularios). */
export const TOKEN_ECONOMICS_DEFAULTS: Required<TokenEconomicsPolicyFields> = {
  measurement_enabled: true,
  estimator_id: 'tiktoken-cl100k_base',
  compare_tolerance_pct: 15,
  estimator_calibration: true,
  count_tokens_sample_pct: 1,
  ledger_retention_days: 30,
  dashboard_window_default: '7d',
  semantic_compression_allow: false,
  platform_quality_holdout_pct: 1,
  optimization_holdout_pct: 0,
  prompt_cache_enabled: true,
  prompt_cache_ttl: 'auto',
  prompt_cache_partition: 'tenant',
  prompt_cache_shared_platform_allowed: false,
  prompt_cache_extended_retention_allowed: false,
  tool_exposure: 'sticky_working_set',
  tool_deferred_threshold_tokens: 10000,
  compaction_trigger_pct: 80,
  compaction_target_pct: 40,
  compaction_on_cold_cache: true,
  output_effort: 'provider_default',
  output_effort_dynamic: false,
  async_service_tier: 'standard',
  async_tier_on_unavailable: 'retry_standard',
  budget_aware_profile: 'off',
  budget_aware_threshold_pct: 80,
};

/** Defaults del baseline (referencia visual en los formularios). */
export const PIPELINE_DEFAULTS: Required<
  Pick<
    PipelinePolicyFields,
    | 'routing_enabled'
    | 'routing_mode'
    | 'routing_on_failure'
    | 'routing_confidence_threshold'
    | 'routing_timeout_ms'
    | 'planning_on_failure'
    | 'grounding_check'
    | 'routing_budget_tokens'
    | 'optimization_enabled'
    | 'max_loop_iterations'
    | 'frontier_agents_enabled'
    | 'frontier_max_duration_minutes'
    | 'frontier_max_concurrent_runs'
    | 'frontier_on_unavailable'
    | 'plan_review'
    | 'retrieval_gating_enabled'
    | 'foreground_timeout_seconds'
    | 'delegation_sync_timeout_seconds'
    | 'delegation_background_timeout_seconds'
    | 'fanout_aggregation_timeout_seconds'
  >
> = {
  routing_enabled: true,
  routing_mode: 'auto',
  routing_on_failure: 'degrade_direct',
  routing_confidence_threshold: 0.6,
  routing_timeout_ms: 1200,
  planning_on_failure: 'skip',
  grounding_check: 'warn',
  routing_budget_tokens: 0,
  optimization_enabled: true,
  max_loop_iterations: 3,
  frontier_agents_enabled: false,
  frontier_max_duration_minutes: 480,
  frontier_max_concurrent_runs: 0,
  frontier_on_unavailable: 'standard',
  plan_review: 'off',
  retrieval_gating_enabled: true,
  foreground_timeout_seconds: 900,
  delegation_sync_timeout_seconds: 300,
  delegation_background_timeout_seconds: 3600,
  fanout_aggregation_timeout_seconds: 3600,
};

/** Todos los defaults (Sprint 278 + economía de tokens) para los formularios. */
export const ALL_PIPELINE_DEFAULTS: Record<string, string | boolean | number> =
  {
    ...(PIPELINE_DEFAULTS as Record<string, string | boolean | number>),
    ...(TOKEN_ECONOMICS_DEFAULTS as Record<string, string | boolean | number>),
  };

// ---------------------------------------------------------------------------
// DR (region) policy — Platform Admin only
// ---------------------------------------------------------------------------

export interface DrLlmPolicy extends PipelinePolicyFields {
  id: string;
  dr_code: string;
  strategy: PolicyStrategy;
  provider_preference: string[];
  provider_weights: Record<string, number>;
  allowed_providers: string[];
  default_data_residency: string[] | null;
  fallback_enabled: boolean;
  fallback_max_attempts: number;
  health_threshold: HealthThreshold;
  policy_enabled: boolean;
  max_agent_steps: number;
  updated_at: string;
  updated_by: string | null;
}

export interface DrLlmPolicyUpdate extends PipelinePolicyFields {
  strategy?: PolicyStrategy;
  provider_preference?: string[];
  provider_weights?: Record<string, number>;
  allowed_providers?: string[];
  default_data_residency?: string[] | null;
  fallback_enabled?: boolean;
  fallback_max_attempts?: number;
  health_threshold?: HealthThreshold;
  policy_enabled?: boolean;
  max_agent_steps?: number;
}

// ---------------------------------------------------------------------------
// Tenant policy — NULL fields mean "inherit from DR"
// ---------------------------------------------------------------------------

export interface TenantLlmPolicy extends PipelinePolicyFields {
  id: string;
  tenant_id: string;
  strategy: PolicyStrategy | null;
  provider_preference: string[] | null;
  provider_weights: Record<string, number> | null;
  allowed_providers: string[] | null;
  /** ISS-701: null = todos los catálogos permitidos. */
  allowed_models: string[] | null;
  byop_policy: ByopPolicy;
  data_residency: string[] | null;
  cost_ceiling_usd_per_1k: string | null;
  fallback_enabled: boolean | null;
  fallback_max_attempts: number | null;
  health_threshold: HealthThreshold | null;
  sticky_session: boolean;
  per_catalog_overrides: Record<string, Record<string, unknown>>;
  allow_emergency_platform_fallback: boolean;
  policy_enabled: boolean | null;
  max_agent_steps: number | null;
  updated_at: string;
  updated_by: string | null;
}

export interface TenantLlmPolicyUpdate extends PipelinePolicyFields {
  strategy?: PolicyStrategy | null;
  provider_preference?: string[] | null;
  provider_weights?: Record<string, number> | null;
  allowed_providers?: string[] | null;
  /** ISS-701: null = todos los catálogos permitidos; lista no vacía = restricción. */
  allowed_models?: string[] | null;
  byop_policy?: ByopPolicy;
  data_residency?: string[] | null;
  cost_ceiling_usd_per_1k?: string | null;
  fallback_enabled?: boolean | null;
  fallback_max_attempts?: number | null;
  health_threshold?: HealthThreshold | null;
  sticky_session?: boolean;
  allow_emergency_platform_fallback?: boolean;
  policy_enabled?: boolean | null;
  max_agent_steps?: number | null;
}

// ---------------------------------------------------------------------------
// Catalog override
// ---------------------------------------------------------------------------

export interface CatalogOverrideUpsert {
  catalog_slug: string;
  strategy?: PolicyStrategy | null;
  provider_preference?: string[] | null;
  provider_weights?: Record<string, number> | null;
  allowed_providers?: string[] | null;
  byop_policy?: ByopPolicy | null;
  data_residency?: string[] | null;
  cost_ceiling_usd_per_1k?: string | null;
  fallback_enabled?: boolean | null;
  fallback_max_attempts?: number | null;
  health_threshold?: HealthThreshold | null;
  sticky_session?: boolean | null;
  allow_emergency_platform_fallback?: boolean | null;
}

// ---------------------------------------------------------------------------
// Effective + simulate
// ---------------------------------------------------------------------------

export interface EffectivePolicy {
  strategy: PolicyStrategy;
  provider_preference: string[];
  provider_weights: Record<string, number>;
  allowed_providers: string[];
  allowed_models: string[] | null;
  byop_policy: ByopPolicy;
  data_residency: string[] | null;
  cost_ceiling_usd_per_1k: string | null;
  sticky_session: boolean;
  fallback_enabled: boolean;
  fallback_max_attempts: number;
  health_threshold: HealthThreshold;
  allow_emergency_platform_fallback: boolean;
  max_agent_steps: number;
  // Sprint 278 §8.1 — valores efectivos con clamps ya aplicados.
  routing_enabled: boolean;
  routing_mode: RoutingMode;
  routing_on_failure: RoutingOnFailure;
  routing_confidence_threshold: number;
  routing_timeout_ms: number;
  planning_on_failure: PlanningOnFailure;
  grounding_check: GroundingCheck;
  routing_budget_tokens: number;
  optimization_enabled: boolean;
  max_loop_iterations: number;
  frontier_agents_enabled: boolean;
  frontier_max_duration_minutes: number;
  frontier_max_concurrent_runs: number;
  frontier_on_unavailable: FrontierOnUnavailable;
  plan_review: PlanReview;
  retrieval_gating_enabled: boolean;
  foreground_timeout_seconds: number;
  delegation_sync_timeout_seconds: number;
  delegation_background_timeout_seconds: number;
  fanout_aggregation_timeout_seconds: number;
  // Sprint 280 §8.1 rev. — valores efectivos con clamps ya aplicados.
  measurement_enabled: boolean;
  estimator_id: EstimatorId;
  compare_tolerance_pct: number;
  estimator_calibration: boolean;
  count_tokens_sample_pct: number;
  ledger_retention_days: number;
  dashboard_window_default: DashboardWindow;
  semantic_compression_allow: boolean;
  platform_quality_holdout_pct: number;
  optimization_holdout_pct: number;
  prompt_cache_enabled: boolean;
  prompt_cache_ttl: PromptCacheTtl;
  prompt_cache_partition: PromptCachePartition;
  prompt_cache_shared_platform_allowed: boolean;
  prompt_cache_extended_retention_allowed: boolean;
  tool_exposure: ToolExposure;
  tool_deferred_threshold_tokens: number;
  compaction_trigger_pct: number;
  compaction_target_pct: number;
  compaction_on_cold_cache: boolean;
  output_effort: OutputEffort;
  output_effort_dynamic: boolean;
  async_service_tier: AsyncServiceTier;
  async_tier_on_unavailable: AsyncTierOnUnavailable;
  budget_aware_profile: BudgetAwareProfile;
  budget_aware_threshold_pct: number;
  source_trace: Record<string, string>;
}

export interface SimulateCatalogResult {
  catalog_slug: string;
  attempts: Array<Record<string, unknown>>;
  empty: boolean;
}

export interface SimulatePolicyRequest {
  catalog_slugs: string[];
  dr_code: string;
  patch?: TenantLlmPolicyUpdate | null;
}

export interface SimulatePolicyResponse {
  tenant_id: string;
  dr_code: string;
  results: SimulateCatalogResult[];
  any_empty: boolean;
}

export const POLICY_STRATEGIES: PolicyStrategy[] = [
  'priority',
  'cost',
  'latency',
  'round_robin',
  'weighted',
  'sticky_tenant',
];

export const BYOP_POLICIES: ByopPolicy[] = [
  'prefer_platform',
  'prefer_byop',
  'only_byop',
  'only_platform',
];
